import { cn } from '@/lib/utils';

import { Divider } from '@/components/atoms/divider';
import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import Footer from '@/components/atoms/layout/footer';
import LinkButtonSB, {
  LinkButtonSBProps,
} from '@/components/atoms/linkButton/LinkSB';
import { Text } from '@/components/atoms/text';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';
import {
  PopupImageLinkStrapi,
  PopupImageLinkStrapiProps,
} from '@/components/molecules/PopupImageLink/PopupImageLinkStrapi';
import { ServiceStatus } from '@/decodo/components/atoms/Instatus';

const getGridTemplatePosition = (index: number) => {
  return [
    'col-start-1 row-start-1',
    'col-start-1 row-start-2',
    'col-start-2 row-start-1 row-span-2',
  ][index];
};

export interface FooterSBProps {
  data: {
    footer: {
      logo: ImageSBProps;
      contacts?: {
        title: string;
        links: PopupImageLinkStrapiProps[];
      }[];
      categories?: {
        title: string;
        links: LinkButtonSBProps[];
      }[];
    };
  };
}

export const getImageLinkTrackerContext = (
  context: string,
  url: string | undefined,
  index: number
) => {
  let result = `${context}-image-link-`;

  if (!url) {
    result += index;
    return result;
  }

  if (url.startsWith('mailto:')) {
    result += url.replace('mailto:', '').split('@')[0];
    return result;
  }

  try {
    const domain = new URL(url).hostname;
    result += domain;

    return result;
  } catch (e) {
    console.error('Error parsing URL', e);
    return (result += 'unknown');
  }
};

export const transformFooterStrapiProps = (data: FooterSBProps['data']) => {
  const { footer } = data;

  const hasFooterLinks =
    !!footer?.contacts?.length || !!footer?.categories?.length;

  return {
    footer,
    hasFooterLinks,
  };
};

export default function FooterSB({ data }: FooterSBProps) {
  const { footer, hasFooterLinks } = transformFooterStrapiProps(data);

  return (
    <Footer>
      <div className={cn(hasFooterLinks && 'mb-8 md:mb-12')}>
        <ImageSB block={footer?.logo} />
      </div>
      <div
        className='flex flex-col flex-nowrap gap-8 md:flex-row md:gap-12'
        data-tracker-section={SECTION_NAMES.FOOTER}
      >
        <div
          className={cn(
            'grid h-fit grid-rows-[auto_auto] gap-8 md:gap-9',
            (footer?.contacts?.length || 0) > 2
              ? 'grid-cols-[160px_160px]'
              : 'grid-cols-[160px]'
          )}
        >
          {footer?.contacts?.map((contact, index) => {
            // in the unlikely scenario we will go with a mix of text/img variants,
            // if at least one link entry contains text, the entire section will be rendered as a list
            const shouldRenderAsList = contact.links.find(
              (link) => !!link.link?.text
            );

            return (
              <div
                key={index}
                className={cn(
                  'flex flex-col gap-6',
                  getGridTemplatePosition(index)
                )}
              >
                <Text variant='button-text-s' color='neutral-N40'>
                  {contact.title}
                </Text>
                <div
                  className={cn(
                    'flex flex-wrap gap-x-4 gap-y-3',
                    shouldRenderAsList && 'flex-col',
                    index === 2 ? 'order-3 sm:order-1' : ''
                  )}
                >
                  {contact.links.map((link, index) => (
                    <PopupImageLinkStrapi
                      key={index}
                      block={link}
                      inner={{
                        link: {
                          variant: 'text',
                          slim: true,
                          'data-tracker-context': `${index}`,
                        },
                        popup: {
                          showArrow: true,
                          position: 'right',
                          classNames: {
                            childrenWrapper:
                              'h-[7.5rem] w-[7.5rem] p-2.5 border-none',
                          },
                        },
                        image: {
                          classNames: {
                            image: 'w-6 h-6',
                          },
                        },
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          <ServiceStatus
            size='sm'
            classNames={{
              // force a slightly wider button width to fit the
              // text that's there 99% of the time - "All systems operational"
              button: cn(
                'w-[200px]',
                footer?.contacts && !footer?.contacts.length && 'mt-6'
              ),
            }}
          />
        </div>
        {!!footer?.categories?.length && (
          <div className='hidden md:block'>
            <Divider alignment='vertical' classNames={{ root: 'mx-3' }} />
          </div>
        )}
        {!!footer?.categories?.length && (
          <div className='flex flex-1 flex-wrap gap-8 md:gap-12'>
            {footer?.categories.map((category, index, arr) => (
              <div
                key={index}
                className={cn(
                  'flex max-w-40 flex-col gap-6',
                  // allow the last category to be smaller, so it follows the designs before flex-wrapping at smaller breakpoints
                  index !== arr.length - 1 && 'min-w-40'
                )}
              >
                <Text variant='button-text-s' color='neutral-N40'>
                  {category.title}
                </Text>
                <div className='flex flex-col gap-3'>
                  {category.links.map((link, index) => (
                    <LinkButtonSB
                      key={index}
                      block={link}
                      inner={{
                        slim: true,
                        textWrap: true,
                        variant: 'text',
                        underline: false,
                        color: 'tertiary',
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Footer>
  );
}
